import React, { useMemo, useState, useEffect } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import Seo, { breadcrumbJsonLd, itemListJsonLd } from "@/components/Seo";
import { ProductCard } from "@/components/common";
import { AdjustableHero, AdjustableInfo } from "@/components/AdjustableSections";
import { PRODUCTS, SIZES, BRANDS, getBrand, ADJUSTABLE_FAQS, LINES } from "@/mock";
import { faqJsonLd } from "@/components/Seo";

const CATEGORY_META = {
  mattresses: {
    title: "Mattresses",
    heading: "Mattresses",
    copy: "Explore premium hybrid, memory foam and innerspring mattresses from the brands you trust.",
  },
  "adjustable-bases": {
    title: "Adjustable Beds & Bases",
    heading: "Adjustable Beds & Bases",
    copy: "Upgrade your sleep with adjustable bases featuring zero-gravity presets, massage and app-controlled positioning.",
  },
  foundations: {
    title: "Mattress Foundations",
    heading: "Foundations",
    copy: "Solid, supportive foundations and box spring alternatives from Serta and Beautyrest, in standard and low-profile heights.",
  },
};

const SORTS = [
  { id: "featured", label: "Featured" },
  { id: "price-asc", label: "Price, low to high" },
  { id: "price-desc", label: "Price, high to low" },
  { id: "name-asc", label: "Alphabetically, A-Z" },
];

function FilterGroup({ title, children }) {
  return (
    <div className="border-b border-neutral-200 py-5">
      <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-neutral-900">{title}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function CheckRow({ label, count, checked, onChange, testid }) {
  return (
    <label className="flex cursor-pointer items-center gap-2.5 text-sm text-neutral-700 hover:text-neutral-900">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        data-testid={testid}
        className="h-4 w-4 rounded border-neutral-300 text-[#D0021B] focus:ring-[#D0021B]"
      />
      <span>
        {label} {count != null && <span className="text-neutral-400">({count})</span>}
      </span>
    </label>
  );
}

export default function Collection({ category = "mattresses" }) {
  const params = useParams();
  const cat = params.category || category;
  const [search] = useSearchParams();
  const q = (search.get("q") || "").toLowerCase();
  const sizeParam = search.get("size") || "";
  const brandParam = search.get("brand") || "";
  const line = LINES[search.get("line") || ""];
  const meta = line ? { title: line.name, heading: line.name, copy: CATEGORY_META.mattresses.copy } : CATEGORY_META[cat] || CATEGORY_META.mattresses;
  const isAdj = cat === "adjustable-bases" && !q;

  const base = useMemo(() => {
    if (line) return PRODUCTS.filter(line.match);
    if (q) {
      const tokens = q.split(/\s+/).filter(Boolean);
      return PRODUCTS.filter((p) => {
        const hay = `${p.name} ${p.type || ""} ${p.brand} ${p.description || ""}`.toLowerCase();
        return tokens.every((t) => hay.includes(t));
      });
    }
    return PRODUCTS.filter((p) => p.category === cat);
  }, [cat, q, line]);

  // Facets
  const brandFacet = useMemo(() => {
    const c = {};
    base.forEach((p) => (c[p.brand] = (c[p.brand] || 0) + 1));
    return Object.keys(c).map((slug) => ({ slug, name: getBrand(slug)?.name || slug, count: c[slug] }));
  }, [base]);
  const typeFacet = useMemo(() => {
    const c = {};
    base.forEach((p) => p.type && (c[p.type] = (c[p.type] || 0) + 1));
    return Object.keys(c).sort().map((t) => ({ t, count: c[t] }));
  }, [base]);
  const priceMax = useMemo(() => Math.max(1000, ...base.map((p) => p.price || 0)), [base]);

  const [brands, setBrands] = useState(new Set(brandParam ? [brandParam] : []));
  const [sizes, setSizes] = useState(new Set(sizeParam ? [sizeParam] : []));
  const [types, setTypes] = useState(new Set());
  const [maxPrice, setMaxPrice] = useState(priceMax);
  const [sort, setSort] = useState("price-asc");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    setBrands(new Set(brandParam ? [brandParam] : []));
    setTypes(new Set());
    setSizes(new Set(sizeParam ? [sizeParam] : []));
    setMaxPrice(priceMax);
    setSort("price-asc");
  }, [cat, q, line, sizeParam, brandParam, priceMax]);

  const toggle = (setter) => (val) =>
    setter((prev) => {
      const n = new Set(prev);
      n.has(val) ? n.delete(val) : n.add(val);
      return n;
    });

  const items = useMemo(() => {
    let list = base.filter((p) => {
      if (brands.size && !brands.has(p.brand)) return false;
      if (types.size && !types.has(p.type)) return false;
      if (sizes.size) {
        const ps = p.sizes && p.sizes.length ? p.sizes : SIZES;
        if (![...sizes].some((s) => ps.includes(s))) return false;
      }
      if ((p.price || 0) > maxPrice) return false;
      return true;
    });
    if (sort === "price-asc") list = [...list].sort((a, b) => (line?.sortKey ? line.sortKey(a) - line.sortKey(b) : 0) || a.price - b.price);
    else if (sort === "price-desc") list = [...list].sort((a, b) => b.price - a.price);
    else if (sort === "name-asc") list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [base, brands, types, sizes, maxPrice, sort]);

  const crumbs = [
    { name: "Home", path: "/" },
    { name: meta.title, path: `/${cat}` },
  ];

  const activeCount = brands.size + types.size + sizes.size + (maxPrice < priceMax ? 1 : 0);
  const clearAll = () => {
    setBrands(new Set());
    setTypes(new Set());
    setSizes(new Set());
    setMaxPrice(priceMax);
  };

  return (
    <>
      <Seo
        title={q ? `Search results` : meta.title}
        description={meta.copy}
        path={`/${cat}`}
        jsonLd={[breadcrumbJsonLd(crumbs), itemListJsonLd(items), ...(isAdj ? [faqJsonLd(ADJUSTABLE_FAQS)] : [])]}
      />

      {isAdj && <AdjustableHero />}

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {!isAdj && (
          <>
            <h1 className="text-3xl font-bold uppercase tracking-tight text-neutral-900 sm:text-4xl">
              {q
                ? `Search: "${q}"`
                : brandParam && cat === "mattresses"
                ? `${getBrand(brandParam)?.name || meta.heading}`
                : meta.heading}
            </h1>
            <p className="mt-2 max-w-3xl text-neutral-600">{meta.copy}</p>
          </>
        )}

        <div className={`grid gap-8 ${isAdj ? "mt-2 lg:grid-cols-[260px_1fr]" : "mt-8 lg:grid-cols-[1fr_260px]"}`}>
          {/* Filters (right on desktop, overlay drawer on mobile) */}
          {showFilters && (
            <div
              className="fixed inset-0 z-40 bg-black/40 lg:hidden"
              onClick={() => setShowFilters(false)}
              aria-hidden="true"
            />
          )}
          <aside
            data-testid="collection-filters"
            className={`${
              showFilters
                ? "fixed inset-y-0 right-0 z-50 w-[85%] max-w-sm overflow-y-auto bg-white p-5 shadow-2xl"
                : "hidden"
            } order-1 lg:static lg:z-auto ${isAdj ? "lg:order-1" : "lg:order-2"} lg:block lg:w-auto lg:max-w-none lg:overflow-visible lg:bg-transparent lg:p-0 lg:shadow-none`}
          >
            <div className="mb-2 flex items-center justify-between lg:hidden">
              <h2 className="text-lg font-bold uppercase text-neutral-900">Filters</h2>
              <button
                type="button"
                onClick={() => setShowFilters(false)}
                data-testid="filter-done"
                className="rounded-full bg-[#D0021B] px-5 py-2 text-sm font-bold uppercase text-white"
              >
                Done
              </button>
            </div>
            <div className="flex items-center justify-between">
              <h2 className="text-base font-bold uppercase text-neutral-900">Filter</h2>
              {activeCount > 0 && (
                <button onClick={clearAll} data-testid="clear-filters" className="text-xs font-semibold text-[#D0021B] hover:underline">
                  Clear ({activeCount})
                </button>
              )}
            </div>

            <FilterGroup title="Price">
              <input
                type="range"
                min={0}
                max={priceMax}
                step={100}
                value={maxPrice}
                onChange={(e) => setMaxPrice(Number(e.target.value))}
                data-testid="filter-price"
                className="w-full accent-[#D0021B]"
              />
              <div className="flex justify-between text-xs text-neutral-500">
                <span>$0</span>
                <span>Up to ${maxPrice.toLocaleString()}</span>
              </div>
            </FilterGroup>

            <FilterGroup title="Size">
              {SIZES.map((s) => (
                <CheckRow key={s} label={s} checked={sizes.has(s)} onChange={() => toggle(setSizes)(s)} testid={`filter-size-${s.toLowerCase().replace(/\s+/g, "-")}`} />
              ))}
            </FilterGroup>

            {typeFacet.length > 0 && (
              <FilterGroup title={cat === "mattresses" ? "Mattress Type" : "Type"}>
                {typeFacet.map(({ t, count }) => (
                  <CheckRow key={t} label={t} count={count} checked={types.has(t)} onChange={() => toggle(setTypes)(t)} testid={`filter-type-${t.toLowerCase().replace(/\s+/g, "-")}`} />
                ))}
              </FilterGroup>
            )}

            {brandFacet.length > 1 && (
              <FilterGroup title="Brand">
                {brandFacet.map(({ slug, name, count }) => (
                  <CheckRow key={slug} label={name} count={count} checked={brands.has(slug)} onChange={() => toggle(setBrands)(slug)} testid={`filter-brand-${slug}`} />
                ))}
              </FilterGroup>
            )}
          </aside>

          {/* Results */}
          <div className={`order-2 ${isAdj ? "lg:order-2" : "lg:order-1"}`}>
            <div className="mb-6 flex flex-wrap items-center justify-between gap-3 border-b border-neutral-200 pb-4">
              <div>
                {isAdj && (
                  <h1 className="text-2xl font-bold text-neutral-900 sm:text-3xl" data-testid="adjustable-heading">
                    Adjustable Beds &amp; Bases
                  </h1>
                )}
                <p className="text-sm text-neutral-600" data-testid="result-count">
                  <span className="font-bold text-neutral-900">{items.length}</span> results
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setShowFilters((v) => !v)}
                  data-testid="mobile-filter-toggle"
                  className="inline-flex items-center gap-2 rounded-lg border border-neutral-300 px-3 py-2 text-sm font-semibold text-neutral-800 lg:hidden"
                >
                  <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
                  Filters{activeCount > 0 ? ` (${activeCount})` : ""}
                </button>
                <label className="flex items-center gap-2 text-sm">
                  <span className="hidden text-neutral-500 sm:inline">Sort</span>
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  data-testid="sort-select"
                  className="rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm font-semibold text-neutral-800 focus:border-[#D0021B] focus:outline-none"
                >
                  {SORTS.map((s) => (
                    <option key={s.id} value={s.id}>{s.label}</option>
                  ))}
                </select>
              </label>
              </div>
            </div>

            {items.length > 0 ? (
              <div className="grid grid-cols-2 gap-5 md:grid-cols-3" data-testid="product-grid">
                {items.map((p) => (<ProductCard key={p.slug} product={p} />))}
              </div>
            ) : (
              <p className="rounded-xl border border-dashed border-neutral-300 bg-neutral-50 p-10 text-center text-neutral-500">
                No products match your filters.{" "}
                <button onClick={clearAll} className="font-semibold text-[#D0021B] hover:underline">Clear filters</button>.
              </p>
            )}
          </div>
        </div>

        {isAdj && <AdjustableInfo />}

        {/* Shop by Brand - internal linking */}
        {cat === "mattresses" && (
          <div className="mt-14">
            <h2 className="text-xl font-bold uppercase text-neutral-900">Shop by Brand</h2>
            <div className="mt-4 flex flex-wrap gap-3">
              {BRANDS.map((b) => (
                <Link key={b.slug} to={`/brands/${b.slug}`} className="rounded-full border border-neutral-300 px-5 py-2 text-sm font-semibold text-neutral-700 hover:border-[#D0021B] hover:text-[#D0021B]">{b.name}</Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
